import React from "react";
import {
  Document, Page, Text, View, StyleSheet,
} from "@react-pdf/renderer";

/**
 * Premium branded PDF renderer for a single Event Document.
 * Matches the platform's Apple/Delta aesthetic — deep black cover,
 * generous whitespace, tight caps overlines.
 *
 * Fonts: Uses only the PDF standard 14 fonts (Helvetica, Helvetica-Bold,
 * Times-Roman, Times-Bold) so rendering NEVER depends on network font
 * fetches. This keeps PDF generation instant and reliable.
 */

const COLORS = {
  black: "#0A0A0A",
  ink: "#111111",
  body: "#333333",
  muted: "#6B6B6B",
  line: "#E4E1D8",
  gold: "#A39171",
  goldSoft: "#F5F1EA",
  cream: "#FBF9F5",
  white: "#FFFFFF",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 56,
    paddingBottom: 64,
    paddingHorizontal: 56,
    fontSize: 10.5,
    color: COLORS.body,
    fontFamily: "Helvetica",
    lineHeight: 1.55,
  },
  coverPage: {
    padding: 0,
    backgroundColor: COLORS.black,
    color: COLORS.white,
    fontFamily: "Helvetica",
  },
  coverInner: {
    padding: 56,
    height: "100%",
    justifyContent: "space-between",
  },
  coverTop: {},
  coverBottom: {},
  overline: {
    fontSize: 8.5,
    letterSpacing: 3,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
  overlineDark: {
    fontSize: 8.5,
    letterSpacing: 3,
    textTransform: "uppercase",
    color: "#8A7A5C",
  },
  goldRule: {
    width: 32,
    height: 1.2,
    backgroundColor: COLORS.gold,
    marginBottom: 10,
  },
  coverTitle: {
    fontSize: 42,
    fontFamily: "Times-Bold",
    color: COLORS.white,
    marginTop: 24,
    lineHeight: 1.05,
  },
  coverSubtitle: {
    marginTop: 20,
    fontSize: 12,
    color: "#CCC5B7",
    lineHeight: 1.6,
    maxWidth: 380,
  },
  coverMetaRow: {
    marginTop: 4,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "rgba(255,255,255,0.12)",
    paddingTop: 24,
  },
  coverMeta: {
    flexDirection: "column",
  },
  coverMetaLabel: {
    fontSize: 7.5,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: "#8A7A5C",
  },
  coverMetaValue: {
    fontSize: 10.5,
    color: COLORS.white,
    marginTop: 5,
  },
  brandLine: {
    fontSize: 8.5,
    letterSpacing: 3,
    textTransform: "uppercase",
    color: "#8A7A5C",
  },
  pageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 0.75,
    borderBottomColor: COLORS.line,
    paddingBottom: 12,
    marginBottom: 28,
  },
  pageHeaderText: {
    fontSize: 7.5,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
  section: {
    marginBottom: 26,
  },
  sectionHeading: {
    fontSize: 20,
    fontFamily: "Times-Bold",
    color: COLORS.ink,
    marginBottom: 4,
  },
  sectionSub: {
    fontSize: 9.5,
    color: COLORS.muted,
    marginBottom: 14,
  },
  paragraph: {
    fontSize: 10.5,
    lineHeight: 1.6,
    color: COLORS.body,
    marginBottom: 12,
  },
  listRow: {
    flexDirection: "row",
    marginBottom: 6,
    paddingRight: 4,
  },
  listMark: {
    width: 14,
    fontSize: 10.5,
    color: COLORS.gold,
  },
  listItem: {
    flex: 1,
    fontSize: 10.5,
    lineHeight: 1.55,
    color: COLORS.body,
  },
  timelineRow: {
    flexDirection: "row",
    marginBottom: 10,
  },
  timelineTime: {
    width: 110,
    fontSize: 9.5,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: COLORS.gold,
    paddingTop: 2,
  },
  timelineBody: {
    flex: 1,
    borderLeftWidth: 1,
    borderLeftColor: COLORS.line,
    paddingLeft: 14,
  },
  timelineTitle: {
    fontSize: 11,
    color: COLORS.ink,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  timelineDesc: {
    fontSize: 10,
    color: COLORS.muted,
    lineHeight: 1.5,
  },
  kvRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.line,
    paddingVertical: 8,
  },
  kvLabel: {
    width: 150,
    fontSize: 8.5,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: COLORS.muted,
    paddingTop: 2,
  },
  kvValue: {
    flex: 1,
    fontSize: 10.5,
    color: COLORS.ink,
  },
  callout: {
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    backgroundColor: COLORS.goldSoft,
    padding: 14,
    marginBottom: 14,
  },
  calloutBlack: {
    backgroundColor: COLORS.black,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    padding: 14,
    marginBottom: 14,
  },
  calloutSoft: {
    backgroundColor: COLORS.cream,
    borderLeftWidth: 3,
    borderLeftColor: "#C6BFAE",
    padding: 14,
    marginBottom: 14,
  },
  calloutTitle: {
    fontSize: 8,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: COLORS.gold,
    marginBottom: 4,
  },
  calloutTitleDark: {
    fontSize: 8,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: "#C6B99A",
    marginBottom: 4,
  },
  calloutText: { fontSize: 10.5, color: COLORS.ink, lineHeight: 1.55 },
  calloutTextDark: { fontSize: 10.5, color: COLORS.white, lineHeight: 1.55 },
  faqItem: { marginBottom: 12 },
  faqQ: { fontSize: 11, fontFamily: "Helvetica-Bold", color: COLORS.ink, marginBottom: 3 },
  faqA: { fontSize: 10.5, color: COLORS.body, lineHeight: 1.55 },
  footer: {
    position: "absolute",
    bottom: 28,
    left: 56,
    right: 56,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.5,
    borderTopColor: COLORS.line,
    paddingTop: 10,
  },
  footerText: {
    fontSize: 7.5,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
});

/* ---------- Block renderers ---------- */

function ParagraphBlock({ block }) {
  const text = (block.text || "").split("\n").filter(Boolean).join("\n");
  return <Text style={styles.paragraph}>{text}</Text>;
}

function ListBlock({ block }) {
  return (
    <View style={{ marginBottom: 12 }}>
      {(block.items || []).map((it, i) => (
        <View key={i} style={styles.listRow} wrap={false}>
          <Text style={styles.listMark}>—</Text>
          <Text style={styles.listItem}>{it}</Text>
        </View>
      ))}
    </View>
  );
}

function TimelineBlock({ block }) {
  return (
    <View style={{ marginBottom: 12 }}>
      {(block.items || []).map((it, i) => (
        <View key={i} style={styles.timelineRow} wrap={false}>
          <Text style={styles.timelineTime}>{it.time}</Text>
          <View style={styles.timelineBody}>
            <Text style={styles.timelineTitle}>{it.title}</Text>
            {it.description ? <Text style={styles.timelineDesc}>{it.description}</Text> : null}
          </View>
        </View>
      ))}
    </View>
  );
}

function KvBlock({ block }) {
  return (
    <View style={{ marginBottom: 12, borderTopWidth: 0.5, borderTopColor: COLORS.line }}>
      {(block.pairs || []).map((p, i) => (
        <View key={i} style={styles.kvRow} wrap={false}>
          <Text style={styles.kvLabel}>{p.label}</Text>
          <Text style={styles.kvValue}>{p.value}</Text>
        </View>
      ))}
    </View>
  );
}

function CalloutBlock({ block }) {
  if (block.tone === "black") {
    return (
      <View style={styles.calloutBlack} wrap={false}>
        {block.title ? <Text style={styles.calloutTitleDark}>{block.title}</Text> : null}
        <Text style={styles.calloutTextDark}>{block.text}</Text>
      </View>
    );
  }
  if (block.tone === "soft") {
    return (
      <View style={styles.calloutSoft} wrap={false}>
        {block.title ? <Text style={styles.calloutTitle}>{block.title}</Text> : null}
        <Text style={styles.calloutText}>{block.text}</Text>
      </View>
    );
  }
  return (
    <View style={styles.callout} wrap={false}>
      {block.title ? <Text style={styles.calloutTitle}>{block.title}</Text> : null}
      <Text style={styles.calloutText}>{block.text}</Text>
    </View>
  );
}

function FaqBlock({ block }) {
  return (
    <View style={{ marginBottom: 12 }}>
      {(block.items || []).map((it, i) => (
        <View key={i} style={styles.faqItem} wrap={false}>
          <Text style={styles.faqQ}>{it.q}</Text>
          <Text style={styles.faqA}>{it.a}</Text>
        </View>
      ))}
    </View>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "paragraph": return <ParagraphBlock block={block} />;
    case "list":      return <ListBlock block={block} />;
    case "timeline":  return <TimelineBlock block={block} />;
    case "kv":        return <KvBlock block={block} />;
    case "callout":   return <CalloutBlock block={block} />;
    case "faq":       return <FaqBlock block={block} />;
    // "image" is intentionally not rendered in the PDF — @react-pdf/renderer
    // needs a network-fetched image and printable resolution; we prefer to
    // reference the image in-line as a note instead.
    case "image":     return null;
    default:          return null;
  }
}

/* ---------- Document ---------- */

export function EventDocPDF({ doc }) {
  if (!doc) return null;

  return (
    <Document title={`${doc.title} — T+L Strategic Supplier Summit`}>
      {/* Cover page */}
      <Page size="LETTER" style={styles.coverPage}>
        <View style={styles.coverInner}>
          <View style={styles.coverTop}>
            <Text style={styles.brandLine}>Travel + Leisure Co.  ·  Strategic Supplier Summit 2026</Text>
            <View style={{ height: 26 }} />
            <View style={styles.goldRule} />
            <Text style={styles.overlineDark}>Official Document</Text>
            <Text style={styles.coverTitle}>{doc.title}</Text>
            {doc.subtitle ? <Text style={styles.coverSubtitle}>{doc.subtitle}</Text> : null}
          </View>
          <View style={styles.coverBottom}>
            <View style={styles.coverMetaRow}>
              <View style={styles.coverMeta}>
                <Text style={styles.coverMetaLabel}>Event Date</Text>
                <Text style={styles.coverMetaValue}>Friday, September 11, 2026</Text>
              </View>
              <View style={styles.coverMeta}>
                <Text style={styles.coverMetaLabel}>Venue</Text>
                <Text style={styles.coverMetaValue}>Travel + Leisure HQ · Orlando</Text>
              </View>
              <View style={styles.coverMeta}>
                <Text style={styles.coverMetaLabel}>Produced by</Text>
                <Text style={styles.coverMetaValue}>Display America</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* Content pages */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.pageHeader} fixed>
          <Text style={styles.pageHeaderText}>{doc.title}</Text>
          <Text style={styles.pageHeaderText}>T+L Strategic Supplier Summit</Text>
        </View>

        {(doc.sections || []).map((s, si) => (
          <View key={s.id || si} style={styles.section} wrap>
            <Text style={styles.sectionHeading}>{s.heading}</Text>
            {s.subheading ? <Text style={styles.sectionSub}>{s.subheading}</Text> : null}
            {(s.blocks || []).map((b, bi) => (
              <Block key={b.id || bi} block={b} />
            ))}
          </View>
        ))}

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Produced by Display America</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}

export default EventDocPDF;
